/**
 * @tyndo/hooks — Materials operator recall console data hooks (MU-6).
 *
 * Thin, typed wrappers over `useApiQuery` / `useApiMutation` for the recall
 * endpoints under `/api/v1/materials/recalls/*`. The recall engine (forward-trace
 * + auto-quarantine, the disposition register, the effectiveness close-out) is
 * entirely server-side; these hooks only read it back and post operator actions.
 *
 * Kept as a dedicated module (rather than inline in the view) so the recall
 * console, any future dashboard widget, and tests share one set of query keys
 * and request shapes. Mirrors the per-feature hook convention used elsewhere in
 * this package (endpoint-bound `useApiMutation`, `enabled`-gated `useApiQuery`).
 */

import type {
  InitiateRecallRequest,
  RecallInitiatedResult,
  RecallListResponse,
  RecallDetail,
  RecordRecallDispositionRequest,
  RecallEffectiveness,
} from '@tyndo/types';

import { useApiQuery, useApiMutation } from './useApi.ts';
import type { ApiError, ApiMutationExtraOptions } from './useApi.ts';

/** Shared mutation callbacks the console passes to drive invalidation + announcements. */
export interface RecallMutationCallbacks<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: ApiError) => void;
}

const RECALLS_ENDPOINT = '/materials/recalls';

/** Build the list endpoint with an optional status filter + clamp params. */
export function buildRecallListEndpoint(opts?: {
  status?: string;
  limit?: number;
  offset?: number;
}): string {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  if (opts?.offset != null) params.set('offset', String(opts.offset));
  const qs = params.toString();
  return qs ? `${RECALLS_ENDPOINT}?${qs}` : RECALLS_ENDPOINT;
}

/** List recalls for the tenant (clamped). Pass a status to filter Open/Closed. */
export function useRecallList(opts?: { status?: string; limit?: number; offset?: number }) {
  return useApiQuery<RecallListResponse>(buildRecallListEndpoint(opts));
}

/** Recall detail (status, impacted lots, dispositions, live effectiveness). */
export function useRecallDetail(recallId: string | null | undefined) {
  const endpoint = recallId ? `${RECALLS_ENDPOINT}/${encodeURIComponent(recallId)}` : '';
  return useApiQuery<RecallDetail>(endpoint, { enabled: Boolean(recallId) });
}

/**
 * Initiate a recall — drives the synchronous forward-trace + auto-quarantine
 * and returns the created recall id + impacted-lot set.
 */
export function useInitiateRecall(
  callbacks?: RecallMutationCallbacks<RecallInitiatedResult>,
  options?: ApiMutationExtraOptions,
) {
  return useApiMutation<RecallInitiatedResult, InitiateRecallRequest>(
    RECALLS_ENDPOINT,
    'POST',
    { ...options, onSuccess: callbacks?.onSuccess, onError: callbacks?.onError },
  );
}

/** Record one impacted lot's disposition on an open recall. */
export function useRecordRecallDisposition(
  recallId: string | null | undefined,
  callbacks?: RecallMutationCallbacks<unknown>,
  options?: ApiMutationExtraOptions,
) {
  const endpoint = recallId
    ? `${RECALLS_ENDPOINT}/${encodeURIComponent(recallId)}/dispositions`
    : `${RECALLS_ENDPOINT}/_/dispositions`;
  return useApiMutation<unknown, RecordRecallDispositionRequest>(
    endpoint,
    'POST',
    { ...options, onSuccess: callbacks?.onSuccess, onError: callbacks?.onError },
  );
}

/** Close a recall and return the effectiveness rollup. */
export function useCloseRecall(
  recallId: string | null | undefined,
  callbacks?: RecallMutationCallbacks<RecallEffectiveness>,
  options?: ApiMutationExtraOptions,
) {
  const endpoint = recallId
    ? `${RECALLS_ENDPOINT}/${encodeURIComponent(recallId)}/close`
    : `${RECALLS_ENDPOINT}/_/close`;
  // The close endpoint takes no body; variables are an empty record.
  return useApiMutation<RecallEffectiveness, Record<string, never>>(
    endpoint,
    'POST',
    { ...options, onSuccess: callbacks?.onSuccess, onError: callbacks?.onError },
  );
}
